import { MapType } from '@/components/common/Providers/GraphqlProvider/generated';

import { CurrentMapElement, GeoJsonFile } from '../../schema';

/** Propiedades inyectadas por prepareCustomMapFile para identificar elementos del mapa */
export const MAP_ELEMENT_ID_KEY = '__mapElementId';
export const MAP_ELEMENT_NAME_KEY = '__mapElementName';

export const mapDescriptors = {
  [MapType.Departmental]: {
    key: 'dpto_ccdgo',
    name: 'dpto_cnmbr',
    file: '/maps/departamentos.json',
  },
  [MapType.Municipal]: { key: 'mpio_cdpmp', name: 'mpio_cnmbr', file: '/maps/municipios.json' },
  [MapType.Custom]: { key: MAP_ELEMENT_ID_KEY, name: MAP_ELEMENT_NAME_KEY, file: '' },
};

/**
 * Detecta si un objeto JSON es un TopoJSON, GeoJSON o un formato no soportado
 * TopoJSON tiene la propiedad "objects" y "arcs"
 * GeoJSON tiene la propiedad "features", "geometries" o es un tipo de geometría válido
 */
export const detectJsonType = (json: any): 'topojson' | 'geojson' | 'invalid' => {
  if (!json || typeof json !== 'object') {
    return 'invalid';
  }

  // Detectar TopoJSON
  if (json.type === 'Topology' && json.objects && json.arcs) {
    return 'topojson';
  }

  // Detectar GeoJSON válido
  const validGeoJsonTypes = [
    'FeatureCollection',
    'GeometryCollection',
    'Feature',
    'Polygon',
    'MultiPolygon',
    'Point',
    'MultiPoint',
    'LineString',
    'MultiLineString',
  ];

  if (validGeoJsonTypes.includes(json.type)) {
    return 'geojson';
  }

  return 'invalid';
};

/**
 * Genera un ID único para un elemento del mapa
 */
const generateMapElementId = (index: number): string => {
  return `region-${index + 1}`;
};

/**
 * Genera un nombre por defecto para un elemento del mapa
 */
const generateMapElementName = (index: number): string => {
  return `Región ${index + 1}`;
};

/**
 * SDK: Prepara un archivo GeoJSON o TopoJSON personalizado inyectando IDs únicos
 * en cada geometría/feature. Esta función debe ser llamada antes de pasar el archivo
 * al hook useMapElements.
 *
 * @param content - Contenido del archivo GeoJSON/TopoJSON como string
 * @returns Objeto con el contenido preparado, la cantidad de elementos y si es válido
 *
 * @example
 * ```ts
 * const { content, elementCount, isValid, error } = prepareCustomMapFile(rawGeoJsonString);
 * if (!isValid) {
 *   console.error(error);
 * }
 * // content ahora tiene __mapElementId y __mapElementName en cada geometría
 * ```
 */
export const prepareCustomMapFile = (
  content: string,
  labelKey?: string
): { content: string; elementCount: number; isValid: boolean; error?: string } => {
  if (!content)
    return { content: '', elementCount: 0, isValid: false, error: 'El contenido está vacío' };

  try {
    const json = JSON.parse(content);
    const jsonType = detectJsonType(json);
    let elementCount = 0;

    if (jsonType === 'invalid') {
      return {
        content: '',
        elementCount: 0,
        isValid: false,
        error: 'El archivo no es un formato GeoJSON o TopoJSON válido',
      };
    }

    if (jsonType === 'topojson') {
      // Inyectar IDs en TopoJSON
      Object.values(json.objects).forEach((obj: any) => {
        if (obj.type === 'GeometryCollection' && obj.geometries) {
          obj.geometries.forEach((geometry: any, index: number) => {
            geometry.properties = geometry.properties || {};
            geometry.properties[MAP_ELEMENT_ID_KEY] = generateMapElementId(elementCount + index);
            geometry.properties[MAP_ELEMENT_NAME_KEY] = labelKey
              ? geometry.properties[labelKey]
              : generateMapElementName(elementCount + index);
          });
          elementCount += obj.geometries.length;
        } else {
          obj.properties = obj.properties || {};
          obj.properties[MAP_ELEMENT_ID_KEY] = generateMapElementId(elementCount);
          obj.properties[MAP_ELEMENT_NAME_KEY] = labelKey
            ? obj.properties[labelKey]
            : generateMapElementName(elementCount);
          elementCount++;
        }
      });
    } else if (jsonType === 'geojson') {
      // Inyectar IDs en GeoJSON
      if (json.type === 'FeatureCollection' && json.features) {
        json.features.forEach((feature: any, index: number) => {
          feature.properties = feature.properties || {};
          feature.properties[MAP_ELEMENT_ID_KEY] = generateMapElementId(index);
          feature.properties[MAP_ELEMENT_NAME_KEY] = labelKey
            ? feature.properties[labelKey]
            : generateMapElementName(index);
        });
        elementCount = json.features.length;
      } else if (json.type === 'GeometryCollection' && json.geometries) {
        json.geometries.forEach((geometry: any, index: number) => {
          geometry.properties = geometry.properties || {};
          geometry.properties[MAP_ELEMENT_ID_KEY] = generateMapElementId(index);
          geometry.properties[MAP_ELEMENT_NAME_KEY] = labelKey
            ? geometry.properties[labelKey]
            : generateMapElementName(index);
        });
        elementCount = json.geometries.length;
      } else if (json.type === 'Feature') {
        json.properties = json.properties || {};
        json.properties[MAP_ELEMENT_ID_KEY] = generateMapElementId(0);
        json.properties[MAP_ELEMENT_NAME_KEY] = labelKey
          ? json.properties[labelKey]
          : generateMapElementName(0);
        elementCount = 1;
      } else if (
        [
          'Polygon',
          'MultiPolygon',
          'Point',
          'MultiPoint',
          'LineString',
          'MultiLineString',
        ].includes(json.type)
      ) {
        json.properties = json.properties || {};
        json.properties[MAP_ELEMENT_ID_KEY] = generateMapElementId(0);
        json.properties[MAP_ELEMENT_NAME_KEY] = labelKey
          ? json.properties[labelKey]
          : generateMapElementName(0);
        elementCount = 1;
      }
    }

    return { content: JSON.stringify(json), elementCount, isValid: true };
  } catch (error) {
    console.error('Error preparing custom map file:', error);
    return {
      content: '',
      elementCount: 0,
      isValid: false,
      error: 'Error al procesar el archivo: JSON inválido',
    };
  }
};

/**
 * Extrae las geometrías de un archivo TopoJSON
 */
export const extractGeometriesFromTopoJSON = (json: any): any[] => {
  return Object.values(json.objects).flatMap((obj: any) => {
    if (obj.type === 'GeometryCollection' && obj.geometries) {
      return obj.geometries;
    }
    return [obj];
  });
};

/**
 * Extrae las features de un archivo GeoJSON
 * Soporta FeatureCollection, GeometryCollection y Feature individual
 */
export const extractFeaturesFromGeoJSON = (json: any): any[] => {
  if (json.type === 'FeatureCollection' && json.features) {
    return json.features;
  }
  if (json.type === 'GeometryCollection' && json.geometries) {
    return json.geometries.map((geometry: any) => ({
      type: 'Feature',
      geometry,
      properties: geometry.properties || {},
    }));
  }
  if (json.type === 'Feature') {
    return [json];
  }
  // Si es una geometría simple (Polygon, MultiPolygon, etc.)
  if (
    ['Polygon', 'MultiPolygon', 'Point', 'MultiPoint', 'LineString', 'MultiLineString'].includes(
      json.type
    )
  ) {
    return [
      {
        type: 'Feature',
        geometry: json,
        properties: json.properties || {},
      },
    ];
  }
  return [];
};

export default function useMapElements({
  elementsConfig,
  mapType,
  departmentCode,
  geoJsonName,
}: {
  elementsConfig: string;
  mapType: MapType;
  departmentCode?: string | null;
  geoJsonName?: string | null;
}) {
  const initializeMapElementsFromPageMapBlock = () => {
    if (!elementsConfig) return [];
    const elements = JSON.parse(elementsConfig) as CurrentMapElement[];
    return elements;
  };

  const initializeMapElementsFromTopoJSON = async (
    currentMapType: MapType,
    currentDepartment?: string
  ) => {
    if (currentMapType && currentMapType !== MapType.Custom) {
      const { file, key, name } = mapDescriptors[currentMapType];

      const response = await fetch(file);
      const json = await response.json();

      const elements = Object.values(json.objects).flatMap((g: any) => {
        return g.geometries
          .filter((geometry: any) => {
            if (currentMapType === MapType.Departmental) return true;
            else {
              return geometry.properties.dpto_ccdgo === currentDepartment;
            }
          })
          .map((geometry: any) => {
            const mapElement: CurrentMapElement = {
              orderIndex: 0,
              id: geometry.properties[key],
              name: geometry.properties[name],
              altName: geometry.properties[name],
              description: geometry.properties[name],
              url: '',
              color: '',
              status: 'ACTIVE',
              extra: false,
            };
            return mapElement;
          });
      });
      return elements;
    }
    return [];
  };

  /**
   * Inicializa elementos del mapa desde un archivo GeoJSON o TopoJSON personalizado.
   * IMPORTANTE: El archivo debe haber sido procesado previamente con prepareCustomMapFile
   * para que contenga las propiedades __mapElementId y __mapElementName.
   */
  const initializeMapElementsFromCustomGeoJSON = (geoJsonContent: string): CurrentMapElement[] => {
    if (!geoJsonContent) return [];

    try {
      const json = JSON.parse(geoJsonContent);
      const jsonType = detectJsonType(json);

      if (jsonType === 'invalid') {
        console.error('El archivo no es un formato GeoJSON o TopoJSON válido');
        return [];
      }

      let geometries: any[] = [];

      if (jsonType === 'topojson') {
        geometries = extractGeometriesFromTopoJSON(json);
      } else {
        geometries = extractFeaturesFromGeoJSON(json);
      }

      const elements: CurrentMapElement[] = geometries.map((item, index) => {
        const properties = item.properties || {};

        const id = properties[MAP_ELEMENT_ID_KEY];
        const name = properties[MAP_ELEMENT_NAME_KEY];

        return {
          orderIndex: index,
          id: String(id),
          name: String(name),
          altName: String(name),
          description: String(name),
          url: '',
          color: '',
          status: 'ACTIVE',
          extra: false,
        };
      });

      return elements;
    } catch (error) {
      console.error('Error parsing custom GeoJSON/TopoJSON:', error);
      return [];
    }
  };

  const createMapElements = async (params: {
    mapType: MapType;
    departmentCode?: string;
    geoJson: GeoJsonFile;
  }) => {
    const key = `${params.mapType}-${params.mapType === MapType.Custom ? params.geoJson.name || '' : params.departmentCode || ''}`;
    const wip = localStorage.getItem(key);
    if (wip) {
      return JSON.parse(wip) as CurrentMapElement[];
    }

    if (elementsConfig && params.mapType === mapType) {
      if (params.mapType === MapType.Departmental) {
        return initializeMapElementsFromPageMapBlock();
      }

      if (params.mapType === MapType.Custom) {
        if (params.geoJson.name === geoJsonName) {
          return initializeMapElementsFromPageMapBlock();
        } else {
          return initializeMapElementsFromCustomGeoJSON(params.geoJson.content);
        }
      }

      if (params.mapType === MapType.Municipal) {
        if (params.departmentCode === departmentCode) {
          return initializeMapElementsFromPageMapBlock();
        } else {
          return await initializeMapElementsFromTopoJSON(params.mapType, params.departmentCode);
        }
      }
    } else {
      if (params.mapType === MapType.Custom) {
        return initializeMapElementsFromCustomGeoJSON(params.geoJson.content);
      } else {
        return await initializeMapElementsFromTopoJSON(params.mapType, params.departmentCode);
      }
    }

    return [];
  };

  return { createMapElements };
}
